// Notes API service — mirrors ideasApi.ts's pattern exactly (sibling feature)

const API_BASE_URL = () => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL || 'https://findashback.onrender.com';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5000';
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    throw new Error('No authentication token found');
  }
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

export interface Note {
  _id?: string;
  title: string;
  content: string;
  color?: 'yellow' | 'blue' | 'red' | 'green';
  userId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const notesApi = {
  async fetchNotes(search?: string): Promise<Note[]> {
    try {
      const queryParams = new URLSearchParams();
      if (search) queryParams.append('search', search);

      const url = `${API_BASE_URL()}/notes${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching notes:', error);
      throw error;
    }
  },

  async createNote(noteData: Omit<Note, '_id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<Note> {
    try {
      const response = await fetch(`${API_BASE_URL()}/notes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(noteData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating note:', error);
      throw error;
    }
  },

  async updateNote(noteId: string, updates: Partial<Note>): Promise<Note> {
    try {
      const response = await fetch(`${API_BASE_URL()}/notes/${noteId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating note:', error);
      throw error;
    }
  },

  async deleteNote(noteId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL()}/notes/${noteId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  }
};
